import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';

// Types
import { TenantDetailLoaderData } from './types';

// GraphQL
import { FETCH_FEATURE_FLAGS } from '../../../../gql/queries/admin';
import {
  SET_CONFIG_FOR_TENANT,
  DELETE_CONFIG_FOR_TENANT,
} from '../../../../gql/mutations/admin';

// Components
import { Typography } from '../../../../components/display/Typography';
import SimpleSwitch from '../../../../components/inputs/buttons/SwitchButton/Simple';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import InputText from '../../../../components/inputs/InputText';
import { isBooleanFlag, isTruthy } from '../flagUtils';

// Styles
import {
  Container,
  AdminSection,
  TenantHeader,
  StatusBadge,
  FlagRow,
  FlagInfo,
  FlagActions,
  OriginBadge,
} from '../tenants/styles';

type FeatureFlagCatalog = {
  id: string;
  key: string;
  description: string;
  defaultValue: string;
  isActive: boolean;
};

export default function TenantDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const loaderData = useAsyncValue() as TenantDetailLoaderData;
  const revalidator = useRevalidator();

  const { tenant, configs } = loaderData;

  // Fetch global feature flag catalog
  const { data: flagsData } = useQuery(FETCH_FEATURE_FLAGS, {
    fetchPolicy: 'cache-first',
  });
  const featureFlags: FeatureFlagCatalog[] = flagsData?.featureFlags || [];

  // Build set of catalog keys for detecting extra flags
  const catalogKeys = new Set(featureFlags.map((f) => f.key));

  // Extra flags in tenant settings that are NOT in the catalog
  const extraFlags = Object.entries(configs).filter(
    ([key]) => !catalogKeys.has(key)
  );

  // Mutations
  const [setConfigForTenant] = useMutation(SET_CONFIG_FOR_TENANT);
  const [deleteConfigForTenant] = useMutation(DELETE_CONFIG_FOR_TENANT);

  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState('');

  const handleSaveValue = useCallback(async () => {
    if (!tenant || !editingKey || !editValue.trim()) return;
    await setConfigForTenant({
      variables: { tenantId: tenant.id, key: editingKey, value: editValue.trim() },
    });
    setEditingKey(null);
    revalidator.revalidate();
  }, [tenant, editingKey, editValue, setConfigForTenant, revalidator]);

  const handleToggleFlag = useCallback(
    async (key: string, currentEnabled: boolean) => {
      if (!tenant) return;
      const newValue = currentEnabled ? 'false' : 'true';
      await setConfigForTenant({
        variables: { tenantId: tenant.id, key, value: newValue },
      });
      revalidator.revalidate();
    },
    [tenant, setConfigForTenant, revalidator]
  );

  const handleDeleteOverride = useCallback(
    async (key: string) => {
      if (!tenant) return;
      await deleteConfigForTenant({
        variables: { tenantId: tenant.id, key },
      });
      revalidator.revalidate();
    },
    [tenant, deleteConfigForTenant, revalidator]
  );

  if (!tenant) {
    return (
      <Container>
        <AdminSection>
          <Typography size="S" variant="regular" colorText="greySilver">
            {t('pages:admin.noTenantSelected')}
          </Typography>
        </AdminSection>
      </Container>
    );
  }

  return (
    <Container>
      {/* Tenant header */}
      <TenantHeader>
        <Typography size="L" variant="semiBold">
          {tenant.name}
        </Typography>
        <StatusBadge $active={tenant.isActive}>
          {tenant.isActive ? t('pages:admin.active') : t('pages:admin.inactive')}
        </StatusBadge>
      </TenantHeader>

      {/* Feature Flags */}
      <AdminSection>
        <Typography size="L" variant="semiBold">
          {t('pages:admin.featureFlags')}
        </Typography>

        {featureFlags.length === 0 && extraFlags.length === 0 ? (
          <Typography size="S" variant="regular" colorText="greySilver">
            {t('pages:admin.noFlags')}
          </Typography>
        ) : (
          <>
            {/* Flags del catálogo global. Booleanos (default true/false) →
                toggle; de valor (p.ej. map_default_center JSON) → texto
                editable. Badge = origen del valor efectivo. */}
            {featureFlags.map((flag) => {
              const tenantValue = configs[flag.key];
              const hasOverride = tenantValue !== undefined;
              const effectiveValue = tenantValue ?? flag.defaultValue;
              const boolFlag = isBooleanFlag(flag.defaultValue);
              const isEditing = editingKey === flag.key;

              return (
                <FlagRow key={flag.key}>
                  <FlagInfo>
                    <Typography size="XS" variant="medium">
                      {flag.key}
                    </Typography>
                    <Typography size="XXS" variant="regular" colorText="greySilver">
                      {flag.description}
                    </Typography>
                    {!boolFlag && !isEditing && (
                      <Typography size="XXS" variant="regular" colorText="greySilver">
                        {t('pages:admin.flagValue')}: {effectiveValue}
                      </Typography>
                    )}
                    {!boolFlag && isEditing && (
                      <InputText
                        label={t('pages:admin.flagValue')}
                        typeInput="normal"
                        inputSize="L"
                        name={`editValue-${flag.key}`}
                        placeholder={flag.defaultValue}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                    )}
                  </FlagInfo>
                  <FlagActions>
                    {!flag.isActive && (
                      <OriginBadge>{t('pages:admin.inactive')}</OriginBadge>
                    )}
                    <OriginBadge $override={hasOverride}>
                      {hasOverride
                        ? t('pages:admin.overrideBadge')
                        : t('pages:admin.defaultBadge')}
                    </OriginBadge>
                    {boolFlag ? (
                      <SimpleSwitch
                        state={isTruthy(effectiveValue)}
                        changeState={() =>
                          handleToggleFlag(flag.key, isTruthy(effectiveValue))
                        }
                      />
                    ) : isEditing ? (
                      <>
                        <MainButton
                          variant="primary"
                          text={t('buttons:save')}
                          onClick={handleSaveValue}
                          disabled={!editValue.trim()}
                        />
                        <MainButton
                          variant="secondary"
                          text={t('buttons:cancel')}
                          onClick={() => {
                            setEditingKey(null);
                            setEditValue('');
                          }}
                        />
                      </>
                    ) : (
                      <MainButton
                        variant="secondary"
                        text={t('buttons:edit')}
                        onClick={() => {
                          setEditingKey(flag.key);
                          setEditValue(effectiveValue);
                        }}
                      />
                    )}
                    {hasOverride && !isEditing && (
                      <MainButton
                        variant="secondary"
                        text={t('pages:admin.inheritDefault')}
                        onClick={() => handleDeleteOverride(flag.key)}
                      />
                    )}
                  </FlagActions>
                </FlagRow>
              );
            })}

            {/* Extra flags no en catálogo — overrides legacy. Misma heurística
                sobre su VALOR (no hay default de catálogo). */}
            {extraFlags.map(([key, value]) => {
              const boolFlag = isBooleanFlag(value);
              const isEditing = editingKey === key;
              return (
                <FlagRow key={key}>
                  <FlagInfo>
                    <Typography size="XS" variant="medium">
                      {key}
                    </Typography>
                    {!boolFlag && !isEditing && (
                      <Typography size="XXS" variant="regular" colorText="greySilver">
                        {t('pages:admin.flagValue')}: {value}
                      </Typography>
                    )}
                    {!boolFlag && isEditing && (
                      <InputText
                        label={t('pages:admin.flagValue')}
                        typeInput="normal"
                        inputSize="L"
                        name={`editValue-${key}`}
                        placeholder={value}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                    )}
                  </FlagInfo>
                  <FlagActions>
                    {boolFlag ? (
                      <SimpleSwitch
                        state={isTruthy(value)}
                        changeState={() => handleToggleFlag(key, isTruthy(value))}
                      />
                    ) : isEditing ? (
                      <>
                        <MainButton
                          variant="primary"
                          text={t('buttons:save')}
                          onClick={handleSaveValue}
                          disabled={!editValue.trim()}
                        />
                        <MainButton
                          variant="secondary"
                          text={t('buttons:cancel')}
                          onClick={() => {
                            setEditingKey(null);
                            setEditValue('');
                          }}
                        />
                      </>
                    ) : (
                      <MainButton
                        variant="secondary"
                        text={t('buttons:edit')}
                        onClick={() => {
                          setEditingKey(key);
                          setEditValue(value);
                        }}
                      />
                    )}
                    <MainButton
                      variant="secondary"
                      text={t('pages:admin.deleteFlag')}
                      onClick={() => handleDeleteOverride(key)}
                    />
                  </FlagActions>
                </FlagRow>
              );
            })}
          </>
        )}
      </AdminSection>
    </Container>
  );
}
